import React, { useEffect, useState } from 'react';
import { UserProfileDto } from '../../Dtos/ClientDashboard/UserProfileDto';
import { useCookies } from 'react-cookie';
import { DEFAULT_HEADERS, to_API_URL } from '../../Services/RestService';

// Updated DTOs based on the new interface definitions
interface ISubscriptionTypeDto {
    id: number;
    title: string;
    priceInCents: number;
    predefinedQuota: IStorageQuotaDto;
    currency: string;
    durationInDays: number;
}

interface IPaymentDto {
    id: number;
    paymentMethod: string;
    receiptId: number;
    priceInCents: number;
    paymentProvider: string;
    externalId: string;
    paidAt: number;
    message: string;
}

interface IStorageQuotaDto {
    id: number;
    totalAllowedStorageInMB: number;
    totalUsedStorageInMB: number;
    attributedAt: Date;
    expiresAt: Date;
}

interface ISubscriptionPlanDto {
    id: number;
    subscriptionType: ISubscriptionTypeDto;
    expiresAt: Date;
    payment: IPaymentDto;
    storageQuota: IStorageQuotaDto;
    nextBillingAt: number;
    enrolledAt: number;
    priceInCents: number;
    subscriptionTitle: string;
    subscriptionDescription: string;
}

// Helper functions
const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatPrice = (priceInCents, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
    }).format(priceInCents / 100);
};

// Helper functions for date comparison
const isSameDay = (date1, date2) => {
    return date1.getDate() === date2.getDate() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getFullYear() === date2.getFullYear();
};

const isWithinInterval = (date, { start, end }) => {
    return date >= start && date <= end;
};

// Custom calendar component since we can't use react-calendar directly
const CustomCalendar = ({ date, subscriptionStart, subscriptionEnd }) => {
    const startDate = new Date(subscriptionStart);
    const endDate = new Date(subscriptionEnd);
    const today = new Date();

    // Get current month and year
    const [viewDate, setViewDate] = useState(new Date(date));
    const currentMonth = viewDate.getMonth();
    const currentYear = viewDate.getFullYear();

    // Helper to get days in month
    const getDaysInMonth = (year, month) => {
        return new Date(year, month + 1, 0).getDate();
    };

    // Get first day of month (0 = Sunday, 1 = Monday, etc.)
    const getFirstDayOfMonth = (year, month) => {
        return new Date(year, month, 1).getDay();
    };

    // Navigate to previous month
    const prevMonth = () => {
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    };

    // Navigate to next month
    const nextMonth = () => {
        setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
    };

    // Generate calendar days
    const generateCalendarDays = () => {
        const daysInMonth = getDaysInMonth(currentYear, currentMonth);
        const firstDayOfMonth = getFirstDayOfMonth(currentYear, currentMonth);
        const days = [];

        // Add empty cells for days before the 1st of month
        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push({ day: null });
        }

        // Add all days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const currentDate = new Date(currentYear, currentMonth, day);

            const isStart = isSameDay(currentDate, startDate);
            const isEnd = isSameDay(currentDate, endDate);
            const isToday = isSameDay(currentDate, today);
            const isInRange = isWithinInterval(currentDate, { start: startDate, end: endDate }) ||
                isStart || isEnd;

            // Determine position in week (0-6)
            const dayOfWeek = (firstDayOfMonth + day - 1) % 7;
            // Determine week number within the month
            const weekNumber = Math.floor((firstDayOfMonth + day - 1) / 7);

            // Determine if this day is at the start or end of a subscription period in its week
            const isStartOfPeriodInWeek = isInRange &&
                (dayOfWeek === 0 || !isWithinInterval(new Date(currentYear, currentMonth, day - 1),
                    { start: startDate, end: endDate }));
            const isEndOfPeriodInWeek = isInRange &&
                (dayOfWeek === 6 || !isWithinInterval(new Date(currentYear, currentMonth, day + 1),
                    { start: startDate, end: endDate }));

            days.push({
                day,
                isStart,
                isEnd,
                isToday,
                isInRange,
                dayOfWeek,
                weekNumber,
                isStartOfPeriodInWeek,
                isEndOfPeriodInWeek
            });
        }

        return days;
    };

    const calendarDays = generateCalendarDays();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];

    // Styles
    const calendarStyles = {
        container: {
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            padding: '16px',
            marginBottom: '16px'
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
        },
        monthTitle: {
            fontSize: '18px',
            fontWeight: 'bold'
        },
        navigationButton: {
            backgroundColor: '#f3f4f6',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 12px',
            cursor: 'pointer',
            color: '#374151'
        },
        weekdays: {
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            textAlign: 'center',
            fontWeight: 'bold',
            marginBottom: '4px'
        },
        weekday: {
            padding: '2px',
            fontSize: '12px',
            color: '#6b7280'
        },
        daysGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '0px'
        },
        dayCell: {
            height: '28px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            fontSize: '13px',
            zIndex: 1
        },
        periodBar: {
            position: 'absolute',
            top: '50%',
            height: '60%',
            backgroundColor: '#e0e7ff',
            transform: 'translateY(-50%)',
            zIndex: 0
        },
        startDay: {
            color: '#10b981',
            fontWeight: 'bold'
        },
        endDay: {
            color: '#ef4444',
            fontWeight: 'bold'
        },
        todayDay: {
            fontWeight: 'bold',
            color: '#3b82f6'
        },
        startIndicator: {
            position: 'absolute',
            top: '50%',
            left: '0',
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: '#10b981',
            transform: 'translate(-30%, -50%)',
            zIndex: 2
        },
        endIndicator: {
            position: 'absolute',
            top: '50%',
            right: '0',
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: '#ef4444',
            transform: 'translate(30%, -50%)',
            zIndex: 2
        },
        todayIndicator: {
            position: 'absolute',
            bottom: '0',
            left: '50%',
            width: '5px',
            height: '5px',
            borderRadius: '50%',
            backgroundColor: '#3b82f6',
            transform: 'translateX(-50%)',
            zIndex: 1
        },
        defaultDay: {
            color: '#1f2937'
        },
        emptyDay: {
            height: '32px'
        }
    };

    return (
        <div style={calendarStyles.container}>
            <div style={calendarStyles.header}>
                <button onClick={prevMonth} style={calendarStyles.navigationButton}>{"<"}</button>
                <div style={calendarStyles.monthTitle}>
                    {monthNames[currentMonth]} {currentYear}
                </div>
                <button onClick={nextMonth} style={calendarStyles.navigationButton}>{">"}</button>
            </div>

            <div style={calendarStyles.weekdays}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
                    <div key={i} style={calendarStyles.weekday}>{day}</div>
                ))}
            </div>

            <div style={calendarStyles.daysGrid}>
                {calendarDays.map((dayInfo, i) => {
                    if (dayInfo.day === null) {
                        return <div key={i} style={calendarStyles.emptyDay}></div>;
                    }

                    const dayCellStyle = { ...calendarStyles.dayCell };
                    let dayNumberStyle = { zIndex: 2, position: 'relative' };

                    // Add specific styling for special days
                    if (dayInfo.isStart) {
                        dayNumberStyle = { ...dayNumberStyle, ...calendarStyles.startDay };
                    } else if (dayInfo.isEnd) {
                        dayNumberStyle = { ...dayNumberStyle, ...calendarStyles.endDay };
                    } else if (dayInfo.isToday) {
                        dayNumberStyle = { ...dayNumberStyle, ...calendarStyles.todayDay };
                    }

                    // Create continuous period bar
                    let periodBarStyle = null;
                    if (dayInfo.isInRange) {
                        periodBarStyle = { ...calendarStyles.periodBar };

                        // Handle start and end of period in a week
                        if (dayInfo.isStartOfPeriodInWeek) {
                            periodBarStyle.left = '50%';
                            periodBarStyle.width = '50%';

                            // If this is the actual start date, extend the bar all the way
                            if (dayInfo.isStart) {
                                periodBarStyle.left = '0%';
                                periodBarStyle.width = '100%';
                            }
                        } else if (dayInfo.isEndOfPeriodInWeek) {
                            periodBarStyle.left = '0';
                            periodBarStyle.width = '50%';

                            // If this is the actual end date, extend the bar all the way
                            if (dayInfo.isEnd) {
                                periodBarStyle.width = '100%';
                            }
                        } else {
                            // Middle of period
                            periodBarStyle.left = '0';
                            periodBarStyle.width = '100%';
                        }
                    }

                    return (
                        <div key={i} style={dayCellStyle}>
                            {dayInfo.isInRange && periodBarStyle && <div style={periodBarStyle}></div>}

                            {/* Day number */}
                            <span style={dayNumberStyle}>
                                {dayInfo.day}
                            </span>

                            {/* Start indicator */}
                            {dayInfo.isStart && <div style={calendarStyles.startIndicator}></div>}

                            {/* End indicator */}
                            {dayInfo.isEnd && <div style={calendarStyles.endIndicator}></div>}

                            {/* Today indicator */}
                            {dayInfo.isToday && <div style={calendarStyles.todayIndicator}></div>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Component to demonstrate the SubscriptionCalendar
const SubscriptionCalendarDemo = (props: { user: UserProfileDto }) => {
    const [subscriptionPlan, setSubscriptionPlan] = useState<ISubscriptionPlanDto | null>(null);
    const [previousSubscriptions, setPreviousSubscriptions] = useState<ISubscriptionPlanDto[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState<IPaymentDto | null>(null);
    const [cookies] = useCookies();


    useEffect(() => {
        fetchCurrentSubscription();
    }, [])

    const fetchPreviousSubscriptions = () => {
        setIsLoading(true);
        fetch(to_API_URL("api/v1/dashboard/users/plans-history?userId=" + props.user.id), {
            method: "GET",
            headers: {
                ...DEFAULT_HEADERS,
                "Authorization": "Bearer " + cookies.accessToken,
            }
        })
            .then(async result => {
                if (result.ok) {
                    const data: ISubscriptionPlanDto[] = await result.json();
                    console.log("previous subscriptions", data);
                    setPreviousSubscriptions(data);
                }

            })
            .catch(err => {
                console.error("Error fetching previous subscriptions:", err);
            })
            .finally(() => {
                setIsLoading(false);
            })
    };

    const fetchCurrentSubscription = async () => {
        setIsLoading(true);
        fetch(to_API_URL("api/v1/dashboard/users/plan?userId=" + props.user.id), {
            method: "GET",
            headers: {
                ...DEFAULT_HEADERS,
                "Authorization": "Bearer " + cookies.accessToken,
            }
        })
            .then(async data => {
                if (data.ok) {
                    const plan: ISubscriptionPlanDto = await data.json();
                    setSubscriptionPlan(plan);

                } else {
                    setSubscriptionPlan(null);
                }
            })
            .catch(err => {
                console.log("failed to load plan", err);
                setSubscriptionPlan(null);
            })
            .finally(() => {
                setIsLoading(false);
            })
    }

    const onPaymentClick = (paymentId: number) => {
        // Find the payment in the previous subscriptions
        const subscription = previousSubscriptions.find(sub => sub.payment.id === paymentId);
        if (subscription) {
            setSelectedPayment(subscription.payment);
        }
    };
    // Styles
    const styles = {
        container: {
            padding: '24px',
            backgroundColor: '#f3f4f6',
            minHeight: '100vh'
        },
        title: {
            fontSize: '20px',
            fontWeight: '500',
            color: '#1f2937',
            marginBottom: '24px'
        },
        card: {
            backgroundColor: '#ffffff',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
            padding: '24px'
        },
        subscriptionHeader: {
            marginBottom: '16px'
        },
        subscriptionTitle: {
            fontSize: '16px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '4px'
        },
        subscriptionDescription: {
            fontSize: '14px',
            color: '#6b7280'
        },
        buttonContainer: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            marginTop: '16px',
            marginBottom: '24px'
        },
        button: {
            padding: '8px 16px',
            borderRadius: '6px',
            border: 'none',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
        },
        upgradeButton: {
            backgroundColor: '#4f46e5',
            color: '#ffffff',
        },
        downgradeButton: {
            backgroundColor: '#f97316',
            color: '#ffffff',
        },
        renewButton: {
            backgroundColor: '#10b981',
            color: '#ffffff',
        },
        expireButton: {
            backgroundColor: '#ef4444',
            color: '#ffffff',
        },
        detailsContainer: {
            marginTop: '24px',
            borderTop: '1px solid #e5e7eb',
            paddingTop: '16px'
        },
        detailsGrid: {
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '16px'
        },
        detailItem: {
            display: 'flex',
            flexDirection: 'column',
            gap: '4px'
        },
        detailLabel: {
            fontSize: '14px',
            fontWeight: '500',
            color: '#6b7280'
        },
        detailValue: {
            fontSize: '14px',
            color: '#1f2937'
        },
        priceBadge: {
            display: 'inline-block',
            marginLeft: '8px',
            padding: '2px 8px',
            backgroundColor: '#f3f4f6',
            borderRadius: '9999px',
            fontSize: '12px',
            color: '#1f2937'
        }
    };

    if (isLoading) return (<div>loading</div>)
    return (
        <div style={styles.container}>
            <h3 style={styles.title}>Monthly Subscription Demo</h3>
            {!subscriptionPlan ?
                <div>no current subscription plan info</div>
                :
                <div style={styles.card}>
                    <div style={styles.subscriptionHeader}>
                        <h4 style={styles.subscriptionTitle}>
                            {subscriptionPlan.subscriptionTitle}
                        </h4>
                        <p style={styles.subscriptionDescription}>
                            {subscriptionPlan.subscriptionDescription}
                        </p>

                        {/* Action Buttons */}
                        {/* <div style={styles.buttonContainer}>
                        <button
                            onClick={() => alert("Upgrade functionality would be implemented here")}
                            style={{ ...styles.button, ...styles.upgradeButton }}
                        >
                            Upgrade
                        </button>
                        <button
                            onClick={() => alert("Downgrade functionality would be implemented here")}
                            style={{ ...styles.button, ...styles.downgradeButton }}
                        >
                            Downgrade
                        </button>
                        <button
                            onClick={() => alert("Renew functionality would be implemented here")}
                            style={{ ...styles.button, ...styles.renewButton }}
                        >
                            Renew
                        </button>
                        <button
                            onClick={() => alert("Expire Now functionality would be implemented here")}
                            style={{ ...styles.button, ...styles.expireButton }}
                        >
                            Expire Now
                        </button>
                    </div> */}
                    </div>

                    {subscriptionPlan && <SubscriptionCalendar subscriptionPlan={subscriptionPlan} />}

                    <div style={styles.detailsContainer}>
                        <div style={styles.detailsGrid}>
                            <div style={styles.detailItem}>
                                <span style={styles.detailLabel}>Storage:</span>
                                <span style={styles.detailValue}>
                                    {subscriptionPlan.storageQuota.totalUsedStorageInMB} MB / {subscriptionPlan.storageQuota.totalAllowedStorageInMB} MB
                                </span>
                            </div>
                            <div style={styles.detailItem}>
                                <span style={styles.detailLabel}>Payment Method:</span>
                                <span style={styles.detailValue}>
                                    {subscriptionPlan.payment.externalId === "DASHBOARD" ? subscriptionPlan.payment.message : subscriptionPlan.payment.paymentMethod} ({subscriptionPlan.payment.paymentProvider})
                                </span>
                            </div>
                        </div>
                    </div>
                </div>}
            <div className="px-4 py-5 sm:px-6 bg-gray-50 flex justify-between items-center">
                <h4 className="text-md font-medium text-gray-700">Previous Subscriptions</h4>
                <button
                    onClick={fetchPreviousSubscriptions}
                    disabled={isLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                    {isLoading ? 'Loading...' : 'Fetch Subscription History'}
                </button>
            </div>
            {previousSubscriptions.length > 0 ? (
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Subscription
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Enrolled Date
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Expired Date
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Price
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Payment
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {previousSubscriptions.map((subscription) => (
                                <tr key={subscription.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {subscription.subscriptionType.title}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatDate(subscription.enrolledAt)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {
                                            formatDate(subscription.expiresAt)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatPrice(subscription.subscriptionType.priceInCents)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <button
                                            onClick={() => onPaymentClick(subscription.payment.id)}
                                            className="text-blue-600 hover:text-blue-900 underline"
                                        >
                                            View Receipt #{subscription.payment.receiptId}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="text-center py-6 text-gray-500">
                    {isLoading ? (
                        <p>Loading subscription history...</p>
                    ) : (
                        <p>No previous subscriptions found or click the button to fetch history.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default SubscriptionCalendarDemo;


const SubscriptionCalendar = ({ subscriptionPlan }: { subscriptionPlan: ISubscriptionPlanDto }) => {
    const start = new Date(subscriptionPlan.enrolledAt);
    const end = new Date(subscriptionPlan.expiresAt);
    const today = new Date();

    // Calculate subscription progress
    const totalDuration = end.getTime() - start.getTime();
    const elapsed = today.getTime() - start.getTime();
    const progress = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));

    // Styles
    const styles = {
        container: {
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            borderBottom: '1px solid #e5e7eb'
        },
        headerTitle: {
            fontSize: '16px',
            fontWeight: '500',
            color: '#374151'
        },
        headerDate: {
            fontSize: '14px',
            color: '#6b7280'
        },
        progressContainer: {
            padding: '12px 16px',
            borderTop: '1px solid #e5e7eb'
        },
        progressLabel: {
            fontSize: '12px',
            color: '#6b7280',
            marginBottom: '4px'
        },
        progressBarOuter: {
            height: '8px',
            backgroundColor: '#e5e7eb',
            borderRadius: '4px',
            overflow: 'hidden'
        },
        progressBarInner: {
            height: '100%',
            backgroundColor: '#10b981',
            borderRadius: '4px',
            width: `${progress}%`
        },
        progressText: {
            fontSize: '12px',
            color: '#6b7280',
            textAlign: 'right',
            marginTop: '4px'
        },
        legend: {
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            padding: '8px 16px',
            borderTop: '1px solid #e5e7eb',
            fontSize: '12px'
        },
        legendItem: {
            display: 'flex',
            alignItems: 'center'
        },
        legendDot: {
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            marginRight: '4px'
        },
        startDot: {
            backgroundColor: '#10b981'
        },
        endDot: {
            backgroundColor: '#ef4444'
        },
        todayDot: {
            backgroundColor: '#3b82f6'
        },
        rangeDot: {
            backgroundColor: '#e0e7ff'
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.headerTitle}>Subscription Period</div>
                <div style={styles.headerDate}>
                    {formatDate(start)} - {formatDate(end)}
                </div>
            </div>

            <CustomCalendar
                date={today}
                subscriptionStart={subscriptionPlan.enrolledAt}
                subscriptionEnd={subscriptionPlan.expiresAt}
            />

            {today > start && today < end && (
                <div style={styles.progressContainer}>
                    <div style={styles.progressLabel}>Subscription Progress</div>
                    <div style={styles.progressBarOuter}>
                        <div style={styles.progressBarInner}></div>
                    </div>
                    <div style={styles.progressText}>
                        {Math.round(progress)}% complete
                    </div>
                </div>
            )}

            <div style={styles.legend}>
                <div style={styles.legendItem}>
                    <div style={{ ...styles.legendDot, ...styles.startDot }}></div>
                    <span>Start</span>
                </div>
                <div style={styles.legendItem}>
                    <div style={{ ...styles.legendDot, ...styles.endDot }}></div>
                    <span>End</span>
                </div>
                <div style={styles.legendItem}>
                    <div style={{ ...styles.legendDot, ...styles.todayDot }}></div>
                    <span>Today</span>
                </div>
                <div style={styles.legendItem}>
                    <div style={{ ...styles.legendDot, ...styles.rangeDot }}></div>
                    <span>Active Period</span>
                </div>
            </div>
        </div>
    );
};

